import { Dispatch, FC, SetStateAction, useState } from "react";
import CustomBurgerMenu from "../../../custom-components/CustomBurgerMenu";
import useScreenSize from "../../../custom-hooks/base-hooks/useScreenSize";
import { Link, useNavigate } from "react-router-dom";
import { MILLISECONDS_IN_MINUTE } from "../../../domain/constants";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { useInterval } from "../../../custom-hooks/base-hooks/useInterval";
import { NotificationServiceImpl } from "../../../services/NotificationServiceImpl";
import { useSelector } from "react-redux";
import { selectShortNotificationsList } from "../../../slices/notifications-slice";
import ApplicationApprovedCard from "./notification-cards/ApplicationApprovedCard";
import ApplicationDeclinedCard from "./notification-cards/ApplicationDeclinedCard";
import ApplicationNewCard from "./notification-cards/ApplicationNewCard";
import SubscriptionActivatedCard from "./notification-cards/SubscriptionActivatedCard";
import SubscriptionReferralCard from "./notification-cards/SubscriptionReferralCard";
import SubscriptionTransactionFailedCard from "./notification-cards/SubscriptionTransactionFailedCard";
import ReviewCreateCard from "./notification-cards/ReviewCreateCard";
import ReviewReceivedCard from "./notification-cards/ReviewReceivedCard";
import { NotificationDTO } from "../../../domain/models/dto/NotificationDTO";
import { NotificationType } from "../../../domain/enums/NotificationTypeEnum";
import NotificationsIcon from "./NotificationsIcon";
import _ from "lodash";
import { useTranslation } from "react-i18next";

interface NotificationsMenuProps {
    setBackgroundBlur: Dispatch<SetStateAction<boolean>>;
    backgroundBlur: boolean;
}

const NotificationsMenu: FC<NotificationsMenuProps> = (props) => {
    const { t } = useTranslation();
    const { moreOrEqual650 } = useScreenSize();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const notifications = useSelector(selectShortNotificationsList);
    useInterval(() => dispatch(NotificationServiceImpl.getShortNotificationsListThunk()), MILLISECONDS_IN_MINUTE);

    const [forceCloseMenu, setForceCloseMenu] = useState(false);

    const getNotificationElement = (notification: NotificationDTO) => {
        const getOnClickCallback = (redirectedUrl: string) => {
            return () => {
                navigate(redirectedUrl);
                dispatch(NotificationServiceImpl.markAsReadNotificationThunk(notification));
            };
        };
        switch (notification.type) {
            case NotificationType.APPLICATION_NEW:
                return (
                    <ApplicationNewCard
                        key={notification.id}
                        notification={notification}
                        onClick={getOnClickCallback(`/work/${_.get(notification, "application.work.id")}`)}
                    />
                );
            case NotificationType.APPLICATION_APPROVED:
                return (
                    <ApplicationApprovedCard
                        key={notification.id}
                        notification={notification}
                        onClick={getOnClickCallback(`/work/${_.get(notification, "application.work.id")}`)}
                    />
                );
            case NotificationType.APPLICATION_DECLINED:
                return (
                    <ApplicationDeclinedCard
                        key={notification.id}
                        notification={notification}
                        onClick={getOnClickCallback(`/work/${_.get(notification, "application.work.id")}`)}
                    />
                );
            case NotificationType.SUBSCRIPTION_ACTIVATED:
                return (
                    <SubscriptionActivatedCard
                        key={notification.id}
                        notification={notification}
                        onClick={getOnClickCallback("/subscription")}
                    />
                );
            case NotificationType.SUBSCRIPTION_REFERRAL:
                return (
                    <SubscriptionReferralCard
                        key={notification.id}
                        notification={notification}
                        onClick={getOnClickCallback("/subscription")}
                    />
                );
            case NotificationType.SUBSCRIPTION_TRANSACTION_FAILED:
                return (
                    <SubscriptionTransactionFailedCard
                        key={notification.id}
                        notification={notification}
                        onClick={getOnClickCallback("/subscription")}
                    />
                );
            case NotificationType.EMPLOYEE_REVIEW_CREATE:
                return (
                    <ReviewCreateCard
                        key={notification.id}
                        notification={notification}
                        onClick={() => setForceCloseMenu(true)}
                    />
                );
            case NotificationType.CLIENT_REVIEW_CREATE:
                return (
                    <ReviewCreateCard
                        key={notification.id}
                        notification={notification}
                        onClick={() => setForceCloseMenu(true)}
                    />
                );
            case NotificationType.REVIEW_RECEIVED:
                return (
                    <ReviewReceivedCard
                        key={notification.id}
                        notification={notification}
                        onClick={() => setForceCloseMenu(true)}
                    />
                );
            default:
                return <></>;
        }
    };

    return (
        <CustomBurgerMenu
            backgroundBlur={props.backgroundBlur}
            setBackgroundBlur={props.setBackgroundBlur}
            popupClassName={`absolute z-[999] bg-backgroundInput right-0 border border-backgroundButton cursor-default h-fit rounded-b-[7px] py-6 px-8 ${
                moreOrEqual650
                    ? "td4:top-[53px] dmin2:top-[72px] w-[560px] dmax2:w-[360px]"
                    : "tmax4:top-[49px] w-[360px] right-[-92px]"
            }`}
            buttonElement={<NotificationsIcon count={notifications.meta.unreadCount} />}
            forceCloseMenu={forceCloseMenu}
            setForceCloseMenu={setForceCloseMenu}
        >
            {notifications.rows.length <= 0 && (
                <div className="H16 dmax2:H14 text-textFaded text-center w-full border-b border-b-textFaded pb-4 mb-3 dmax2:mb-2">
                    {t("notifications.noNewMessages")}
                </div>
            )}
            {notifications.rows.map(getNotificationElement)}
            <Link to="/notifications" className="block H20B text-textWhite text-center mt-[10px] cursor-pointer">
                {t("notifications.allMessages")}
            </Link>
        </CustomBurgerMenu>
    );
};

export default NotificationsMenu;
